import { Capacitor } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { FileViewer } from '@capacitor/file-viewer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

const triggerAnchorDownload = (href: string, downloadName?: string) => {
  const a = document.createElement('a');
  a.href = href;
  a.style.display = 'none';
  a.setAttribute('download', downloadName || `download-${uuid()}`);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadFile = async ({
  fileUrl,
  fileName,
}: {
  fileUrl: string;
  fileName?: string;
}): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    const fileInfo = await Filesystem.getUri({
      directory: Directory.Documents,
      path: `${uuid()}_${fileUrl.split('/').pop() ?? uuid()}`,
    });

    const path = fileInfo.uri;

    await FileTransfer.downloadFile({
      url: fileUrl,
      path,
    });

    await FileViewer.openDocumentFromLocalPath({
      path,
    });
  } else {
    // Blob-first: ensures browser 'download' event for tests; fallback to direct link on CORS/other errors
    try {
      // Ref: https://medium.com/charisol-community/downloading-resources-in-html5-a-download-may-not-work-as-expected-bf63546e2baa
      const fileBlob = (await axios.get(fileUrl, { responseType: 'blob' })).data;
      const fileBlobUrl = URL.createObjectURL(fileBlob);
      triggerAnchorDownload(fileBlobUrl, fileName);
      setTimeout(() => URL.revokeObjectURL(fileBlobUrl), 100);
    } catch {
      const u = new URL(fileUrl);
      const disposition = fileName
        ? `attachment; filename="${encodeURIComponent(fileName)}"`
        : 'attachment';
      u.searchParams.set('response-content-disposition', disposition);
      triggerAnchorDownload(u.toString(), fileName);
    }
  }
};
